/**
 * Product Sales Report Page Component
 *
 * Main page for the Product Sales Report feature
 * Displays product sales report with filters, summary cards, and products table
 */

import React, {useEffect} from 'react';
import SidebarLayout from '../../dashboard/components/SidebarLayout';
import {useProductSalesReport} from '../hooks/useProductSalesReport';
import {PageLoadingState} from '../../../Presentation/components/loading/PageLoadingState';
import {InlineLoadingState} from '../../../Presentation/components/loading/InlineLoadingState';
import {AppTitle, AppText, AppCardTitle} from '../../../Presentation/components/ui/Typography';
import Card from '../../../Presentation/components/ui/Card';
import {ProductSalesDateFilter} from '../components/ProductSalesDateFilter';
import {ProductSalesSummaryCards} from '../components/ProductSalesSummaryCards';
import {ProductSalesTable} from '../components/ProductSalesTable';
import {useTableExport, useExportConfig} from '../../../Presentation/hooks/useTableExport';
import ExportButton from '../../../Presentation/components/ui/ExportButton';
import {ExportFormatters} from '../../../Presentation/utils/exportFormatters';
import Button from '../../../Presentation/components/ui/Button';
import {RefreshCw} from 'lucide-react';

const ProductSalesReportPage: React.FC = () => {
  const {
    reportData,
    isLoading,
    error,
    filters,
    getProductSalesReport,
    setFilters,
    clearError
  } = useProductSalesReport();

  // Fetch data on mount and when filters change
  useEffect(() => {
    getProductSalesReport(filters).catch(() => {
      // Error handling is done in the hook
    });
  }, [filters, getProductSalesReport]);

  // Export configuration
  const {createConfig} = useExportConfig<any>();
  const exportConfig = createConfig([
    {key: 'product_name', title: 'اسم المنتج'},
    {key: 'product_brand', title: 'العلامة التجارية'},
    {key: 'product_category', title: 'الفئة'},
    {key: 'quantity_sold', title: 'الكمية المباعة'},
    {key: 'unit_price', title: 'سعر الوحدة', formatter: ExportFormatters.currencySARExport},
    {key: 'total_revenue', title: 'إجمالي الإيرادات', formatter: ExportFormatters.currencySARExport},
    {key: 'stores_count', title: 'عدد المتاجر'},
    {key: 'orders_count', title: 'عدد الطلبات'}
  ], 'تقرير مبيعات المنتجات', 'تصدير تقرير مبيعات المنتجات', 'تصدير بيانات مبيعات المنتجات');

  const exportHook = useTableExport({
    data: reportData?.product_sales || [],
    config: exportConfig
  });

  const handleRefresh = () => {
    getProductSalesReport(filters).catch(() => {
      // Error handling is done in the hook
    });
  };

  const handleFiltersChange = (newFilters: typeof filters) => {
    setFilters(newFilters);
  };

  if (isLoading && !reportData) {
    return (
      <SidebarLayout>
        <div className="h-full flex items-center justify-center">
          <PageLoadingState message="جاري تحميل تقرير مبيعات المنتجات..." />
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="h-full flex flex-col p-6">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <AppTitle className="text-3xl font-bold text-gray-900 mb-2">
                تقرير مبيعات المنتجات
              </AppTitle>
              <AppText className="text-gray-600">
                عرض تفاصيل مبيعات جميع المنتجات مع الإحصائيات والتحليلات
              </AppText>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={handleRefresh}
                variant="outline"
                size="medium"
                disabled={isLoading}
                className="flex items-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                تحديث
              </Button>
              {reportData && reportData.product_sales.length > 0 && (
                <ExportButton
                  onExport={exportHook.exportData}
                  isExporting={exportHook.isExporting}
                  canExport={exportHook.canExport}
                  recordCount={exportHook.recordCount}
                  showDropdown={true}
                />
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="space-y-6">
            {/* Date Filters */}
            <ProductSalesDateFilter
              filters={filters}
              onFiltersChange={handleFiltersChange}
            />

            {/* Loading State */}
            {isLoading && reportData && (
              <div className="py-8">
                <InlineLoadingState message="جاري تحديث البيانات..." />
              </div>
            )}

            {/* Error State */}
            {error && (
              <Card className="p-6 bg-red-50 border-red-200">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="flex-1">
                    <AppText className="text-red-800">{error}</AppText>
                  </div>
                  <Button
                    onClick={() => {
                      clearError();
                      handleRefresh();
                    }}
                    variant="outline"
                    size="small"
                  >
                    إعادة المحاولة
                  </Button>
                </div>
              </Card>
            )}

            {/* Summary Cards */}
            {reportData && reportData.summary && (
              <ProductSalesSummaryCards summary={reportData.summary} />
            )}

            {/* Products Table */}
            {reportData && reportData.product_sales.length > 0 ? (
              <Card>
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex justify-between items-center">
                    <AppCardTitle className="text-gray-900">
                      قائمة المنتجات ({reportData.product_sales.length} منتج)
                    </AppCardTitle>
                  </div>
                </div>
                <div className="p-6">
                  <ProductSalesTable products={reportData.product_sales} />
                </div>
              </Card>
            ) : reportData && reportData.product_sales.length === 0 ? (
              <Card className="p-12 text-center">
                <div className="text-gray-400 mb-4">
                  <svg className="mx-auto h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <AppCardTitle className="text-gray-500 mb-2">
                  لا توجد بيانات متاحة
                </AppCardTitle>
                <AppText className="text-gray-400">
                  لا توجد منتجات في الفترة المحددة. جرب تغيير الفترة الزمنية.
                </AppText>
              </Card>
            ) : null}
          </div>
        </div>
      </div>
    </SidebarLayout>
  );
};

export default ProductSalesReportPage;

